import { useState, type FormEvent } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { Seo } from '@/components/Seo';
import { submitPartnerApplication, REQUIRED_DOC_KEYS, OPTIONAL_DOC_KEYS } from '@/lib/partnerApplications';
import type { PartnerDocFiles, PartnerDocKey } from '@/lib/partnerApplications';
import { ICONS, Icon, InfoShell, usePrefix } from '@/pages/info/v2/InfoShell';
import './partner-v2.css';

// New-design partner application (/partner-apply). Same fields, documents
// and submitPartnerApplication() as the classic page, laid out in three
// numbered parts (business, location, documents) beside a sticky benefits
// panel. Text uses the classic partnerApply.* keys.

const DOC_ICON: Record<string, string | string[]> = {
  drug_license: ICONS.doc,
  tin: ICONS.doc,
  nid: ICONS.shield,
  photo: ['M4 7h4l2-2h4l2 2h4v12H4z', 'M12 16.5a3.5 3.5 0 1 0 0-7 3.5 3.5 0 0 0 0 7z'],
  bin: ICONS.doc,
  bank_solvency: ICONS.building,
  bank_statement: ICONS.doc,
};

export default function PartnerApplyV2() {
  const { t } = useTranslation();
  const prefix = usePrefix();
  const navigate = useNavigate();
  const [submitting, setSubmitting] = useState(false);
  const [submitError, setSubmitError] = useState<string | null>(null);
  const [files, setFiles] = useState<Partial<Record<PartnerDocKey, string>>>({});
  const [why, setWhy] = useState(0);

  const isRequired = (key: PartnerDocKey) => (REQUIRED_DOC_KEYS as readonly string[]).includes(key);

  const doc = (key: PartnerDocKey, labelKey: string, numberField?: { name: string; labelKey: string }) => {
    const required = isRequired(key);
    const chosen = files[key];
    return (
      <div className="pdoc" key={key}>
        {numberField && (
          <label>
            <span>
              {t(numberField.labelKey)} {required && '*'}
            </span>
            <input name={numberField.name} required={required} type="text" />
          </label>
        )}
        <label className={`pdrop${chosen ? ' on' : ''}`}>
          <input
            name={key}
            type="file"
            accept="image/jpeg,image/png,image/webp,application/pdf"
            required={required}
            onChange={(e) => setFiles((f) => ({ ...f, [key]: e.target.files?.[0]?.name || undefined }))}
          />
          <i>
            <Icon d={chosen ? ICONS.check : DOC_ICON[key] || ICONS.doc} />
          </i>
          <span className="pdt">
            <b>
              {t(labelKey)} {required && <em>*</em>}
            </b>
            <small>{chosen || t('infoV2.partner.pick', 'JPG, PNG or PDF · tap to choose')}</small>
          </span>
        </label>
      </div>
    );
  };

  const onSubmit = async (e: FormEvent<HTMLFormElement>) => {
    e.preventDefault();
    setSubmitting(true);
    setSubmitError(null);
    const data = new FormData(e.currentTarget);
    const get = (k: string) => (data.get(k) as string | null)?.trim() || null;
    const docs: PartnerDocFiles = {};
    for (const key of [...REQUIRED_DOC_KEYS, ...OPTIONAL_DOC_KEYS]) {
      const file = data.get(key) as File | null;
      if (file && file.size > 0) docs[key] = file;
    }
    const { error } = await submitPartnerApplication(
      {
        business_name: get('businessName') ?? '',
        business_type: get('businessType'),
        contact_person: get('contactPerson') ?? '',
        email: get('email') ?? '',
        phone: get('phone') ?? '',
        city: get('city'),
        address: get('address'),
        map_link: get('mapLink'),
        monthly_volume: get('volume'),
        why_partner: get('whyPartner'),
        drug_license_no: get('drugLicenseNo') ?? '',
        tin_no: get('tinNo') ?? '',
        nid_no: get('nidNo') ?? '',
        bin_no: get('binNo'),
      },
      docs,
    );
    if (error) {
      setSubmitting(false);
      setSubmitError(error);
      return;
    }
    navigate(prefix + '/partner-apply/success');
  };

  const benefits = [
    { icon: ICONS.gift, key: 'partnerApply.benefits.brandSupport' },
    { icon: ICONS.flask, key: 'partnerApply.benefits.training' },
    { icon: ICONS.shield, key: 'partnerApply.benefits.exclusive' },
    { icon: ['M4 19V5', 'M4 19h16', 'M8 15l4-4 3 3 5-6'], key: 'partnerApply.benefits.margin' },
  ];
  const steps = [
    { n: '01', label: t('infoV2.partner.s1', 'Apply online') },
    { n: '02', label: t('infoV2.partner.s2', 'Review in 3 business days') },
    { n: '03', label: t('infoV2.partner.s3', 'Start selling') },
  ];

  return (
    <>
      <Seo pageKey="partnerApply" />
      <InfoShell
        nav={false}
        help={false}
        label={t('partnerApply.hero.label')}
        title={t('partnerApply.hero.heading')}
        lede={t('partnerApply.hero.subtitle')}
        meta={
          <ol className="psteps">
            {steps.map((s) => (
              <li key={s.n}>
                <em>{s.n}</em>
                <span>{s.label}</span>
              </li>
            ))}
          </ol>
        }
      >
        <section className="isec soft">
          <div className="wrap play">
            <form className="pform rv" onSubmit={onSubmit}>
              {/* 1. Business */}
              <fieldset>
                <legend>
                  <em>01</em>
                  {t('infoV2.partner.business', 'Your business')}
                </legend>
                <div className="ifm">
                  <label>
                    <span>{t('partnerApply.form.businessName')} *</span>
                    <input name="businessName" required type="text" />
                  </label>
                  <label>
                    <span>{t('partnerApply.form.businessType')} *</span>
                    <select name="businessType" required defaultValue="">
                      <option value="">{t('common.select')}</option>
                      <option value="pharmacy">{t('partnerApply.form.typePharmacy')}</option>
                      <option value="beauty">{t('partnerApply.form.typeBeauty')}</option>
                      <option value="online">{t('partnerApply.form.typeOnline')}</option>
                      <option value="distributor">{t('partnerApply.form.typeDistributor')}</option>
                    </select>
                  </label>
                  <label>
                    <span>{t('partnerApply.form.contactPerson')} *</span>
                    <input name="contactPerson" required type="text" />
                  </label>
                  <label>
                    <span>{t('partnerApply.form.phone')} *</span>
                    <input name="phone" required type="text" inputMode="tel" placeholder="+880" />
                  </label>
                  <label className="full">
                    <span>{t('partnerApply.form.email')} *</span>
                    <input name="email" required type="email" />
                  </label>
                </div>
              </fieldset>

              {/* 2. Location */}
              <fieldset>
                <legend>
                  <em>02</em>
                  {t('infoV2.partner.location', 'Location & volume')}
                </legend>
                <div className="ifm">
                  <label>
                    <span>{t('partnerApply.form.city')} *</span>
                    <select name="city" required defaultValue="">
                      <option value="">{t('common.select')}</option>
                      <option value="dhaka">{t('partnerApply.form.cityDhaka')}</option>
                      <option value="chittagong">{t('partnerApply.form.cityChittagong')}</option>
                      <option value="sylhet">{t('partnerApply.form.citySylhet')}</option>
                      <option value="others">{t('partnerApply.form.cityOthers')}</option>
                    </select>
                  </label>
                  <label>
                    <span>{t('partnerApply.form.volume')}</span>
                    <select name="volume" defaultValue="">
                      <option value="">{t('common.select')}</option>
                      <option value="<100">{t('partnerApply.form.volume1')}</option>
                      <option value="100-500">{t('partnerApply.form.volume2')}</option>
                      <option value="500-1000">{t('partnerApply.form.volume3')}</option>
                      <option value="1000+">{t('partnerApply.form.volume4')}</option>
                    </select>
                  </label>
                  <label className="full">
                    <span>{t('partnerApply.form.address')} *</span>
                    <input name="address" required type="text" />
                  </label>
                  <label className="full">
                    <span>{t('partnerApply.form.mapLink')}</span>
                    <input name="mapLink" type="text" inputMode="url" placeholder="https://maps.app.goo.gl/…" />
                    <em className="hint">{t('partnerApply.form.mapLinkHint')}</em>
                  </label>
                  <label className="full">
                    <span>{t('partnerApply.form.whyPartner')}</span>
                    <textarea name="whyPartner" rows={4} maxLength={500} placeholder={t('partnerApply.form.whyPlaceholder')} onChange={(e) => setWhy(e.target.value.length)} />
                    <em>{why} / 500</em>
                  </label>
                </div>
              </fieldset>

              {/* 3. Documents */}
              <fieldset>
                <legend>
                  <em>03</em>
                  {t('partnerApply.form.documentsTitle')}
                </legend>
                <div className="pdocs">
                  {doc('drug_license', 'partnerApply.form.docDrugLicense', { name: 'drugLicenseNo', labelKey: 'partnerApply.form.drugLicenseNo' })}
                  {doc('tin', 'partnerApply.form.docTin', { name: 'tinNo', labelKey: 'partnerApply.form.tinNo' })}
                  {doc('nid', 'partnerApply.form.docNid', { name: 'nidNo', labelKey: 'partnerApply.form.nidNo' })}
                  {doc('photo', 'partnerApply.form.docPhoto')}
                  {doc('bin', 'partnerApply.form.docBin', { name: 'binNo', labelKey: 'partnerApply.form.binNo' })}
                  {doc('bank_solvency', 'partnerApply.form.docBankSolvency')}
                  {doc('bank_statement', 'partnerApply.form.docBankStatement')}
                </div>
              </fieldset>

              {submitError && <p className="perr">{submitError}</p>}
              <button type="submit" className="btn dark psub" disabled={submitting}>
                {submitting ? t('common.submitting') : t('partnerApply.form.submit')}
                <Icon d={ICONS.arrow} />
              </button>
            </form>

            <aside className="pside rv" style={{ ['--d' as string]: '.1s' }}>
              <div className="pben">
                <span className="lbl">{t('partnerApply.benefits.title')}</span>
                <ul>
                  {benefits.map((b) => (
                    <li key={b.key}>
                      <span className="ic">
                        <Icon d={b.icon} />
                      </span>
                      {t(b.key)}
                    </li>
                  ))}
                </ul>
              </div>
              <div className="pask">
                <b>{t('contact.info.title')}</b>
                <p>{t('faq.contactUs', 'Questions about partnering? Our team is happy to help.')}</p>
                <Link to={prefix + '/contact'} className="cmore">
                  {t('common.contact')}
                  <Icon d={ICONS.arrow} />
                </Link>
              </div>
            </aside>
          </div>
        </section>
      </InfoShell>
    </>
  );
}
